import React, { useContext, useMemo, useState } from "react";
import { ThemeContext } from "../context/ThemeContext";

type ModeKey = "focus" | "recovery" | "training" | "learning" | "motivation" | "sleep";

type AvaSpace = {
  key: ModeKey;
  title: string;
  purpose: string;
  examples: string[];
  status: "Available" | "Coming Soon";
};

type ApprovedMedia = {
  id: string;
  modeKey: ModeKey;
  title: string;
  mode: string;
  description: string;
  youtubeId: string;
  safeForYoung: boolean;
};

const spaces: AvaSpace[] = [
  {
    key: "focus",
    title: "Focus Mode",
    purpose: "Calm sounds, ambient visuals, and deep-work energy.",
    examples: ["focus audio", "ambient cyber visuals", "study support"],
    status: "Available",
  },
  {
    key: "recovery",
    title: "Recovery Mode",
    purpose: "Relaxing, stress-reducing, all-ages wellness media.",
    examples: ["breathing", "nature", "calming music"],
    status: "Available",
  },
  {
    key: "training",
    title: "Training Mode",
    purpose: "Football, baseball, soccer, hockey, wrestling, martial arts, and Jeet Kune Do technique without graphic violence.",
    examples: [
      "football",
      "baseball",
      "soccer",
      "hockey",
      "wrestling",
      "martial arts",
      "Jeet Kune Do"
    ],
    status: "Available",
  },
  {
    key: "learning",
    title: "Learning Mode",
    purpose: "Science, space, creativity, and educational creator content.",
    examples: ["space", "science", "learning videos"],
    status: "Available",
  },
  {
    key: "motivation",
    title: "Motivation Mode",
    purpose: "Positive, clean motivational media selected for Ava’s tone.",
    examples: ["confidence", "goals", "daily reset"],
    status: "Coming Soon",
  },
  {
    key: "sleep",
    title: "Sleep Mode",
    purpose: "Low-light relaxing experiences for winding down.",
    examples: ["soft music", "dark visuals", "sleep prep"],
    status: "Coming Soon",
  },
];

const approvedMedia: ApprovedMedia[] = [
  {
    id: "focus-visual",
    modeKey: "focus",
    title: "Focus Visual Session",
    mode: "Focus Mode",
    description: "A calm focus video slot for approved ambient/focus content.",
    youtubeId: "jfKfPfyJRdk",
    safeForYoung: true,
  },
  {
    id: "focus-deep-work",
    modeKey: "focus",
    title: "Focus Deep Work Session",
    mode: "Focus Mode",
    description: "A second approved focus slot for studying, coding, or calm work.",
    youtubeId: "DWcJFNfaw9c",
    safeForYoung: true,
  },
  {
    id: "recovery-relax",
    modeKey: "recovery",
    title: "Recovery / Relax Session",
    mode: "Recovery Mode",
    description: "A relaxing video slot for approved breathing, nature, or calming music.",
    youtubeId: "inpok4MKVLM",
    safeForYoung: true,
  },
  {
    id: "recovery-breathing",
    modeKey: "recovery",
    title: "Calm Breathing Session",
    mode: "Recovery Mode",
    description: "A second approved recovery slot for breathing and stress relief.",
    youtubeId: "SEfs5TJZ6Nk",
    safeForYoung: true,
  },
  {
    id: "training-general",
    modeKey: "training",
    title: "General Athletic Training",
    mode: "Training Mode",
    description: "Approved athletic movement and safe training fundamentals.",
    youtubeId: "ml6cT4AZdqI",
    safeForYoung: true,
  },
  {
    id: "training-wrestling",
    modeKey: "training",
    title: "Wrestling Fundamentals",
    mode: "Training Mode",
    description: "Approved wrestling fundamentals focused on youth-safe technique and discipline.",
    youtubeId: "LFIa7fxuQPo",
    safeForYoung: true,
  },
  {
    id: "training-wrestling-takedown",
    modeKey: "training",
    title: "Wrestling Takedown Basics",
    mode: "Training Mode",
    description: "Approved wrestling takedown basics focused on controlled technique.",
    youtubeId: "UZHMkCYDnu4",
    safeForYoung: true,
  },
  {
    id: "training-football",
    modeKey: "training",
    title: "Football Fundamentals",
    mode: "Training Mode",
    description: "Approved football fundamentals focused on safe drills, footwork, and discipline.",
    youtubeId: "wWeZ7ScEq04",
    safeForYoung: true,
  },
  {
    id: "training-baseball",
    modeKey: "training",
    title: "Baseball Fundamentals",
    mode: "Training Mode",
    description: "Approved baseball fundamentals focused on stance, throwing, fielding, and safe practice.",
    youtubeId: "51lIXKsxM8w",
    safeForYoung: true,
  },
  {
    id: "training-soccer",
    modeKey: "training",
    title: "Soccer Fundamentals",
    mode: "Training Mode",
    description: "Approved soccer fundamentals focused on footwork, control, passing, and safe drills.",
    youtubeId: "K63Um58qm08",
    safeForYoung: true,
  },
  {
    id: "training-hockey",
    modeKey: "training",
    title: "Hockey Fundamentals",
    mode: "Training Mode",
    description: "Approved hockey fundamentals focused on balance, movement, control, and safe training.",
    youtubeId: "85JCRFO7-wM",
    safeForYoung: true,
  },
  {
    id: "training-martial-arts",
    modeKey: "training",
    title: "Martial Arts Basics",
    mode: "Training Mode",
    description: "Approved martial arts basics focused on balance, discipline, movement, and safe technique.",
    youtubeId: "xguK9YehaLo",
    safeForYoung: true,
  },
  {
    id: "training-jkd",
    modeKey: "training",
    title: "Jeet Kune Do Basics",
    mode: "Training Mode",
    description: "Approved Jeet Kune Do basics focused on form, movement, discipline, and safe technique.",
    youtubeId: "VlOzdvDsU9g",
    safeForYoung: true,
  },
  {
    id: "learning-space",
    modeKey: "learning",
    title: "Learning / Space Session",
    mode: "Learning Mode",
    description: "A clean educational or science video slot.",
    youtubeId: "libKVRa01L8",
    safeForYoung: true,
  },
  {
    id: "learning-science",
    modeKey: "learning",
    title: "Science Learning Session",
    mode: "Learning Mode",
    description: "A second approved learning slot for science and curiosity.",
    youtubeId: "0rHUDWjR5gg",
    safeForYoung: true,
  },
];

const blockedExamples = [
  "explicit profanity",
  "graphic violence",
  "sexual/adult content",
  "gore or shock content",
  "unsafe challenges",
  "extremist content",
  "uncurated random embeds",
];

export default function Entertainment() {
  const { themeColors } = useContext(ThemeContext);
  const [youngSafeMode, setYoungSafeMode] = useState(true);
  const [selectedMode, setSelectedMode] = useState<ModeKey>("focus");
  const [selectedMedia, setSelectedMedia] = useState<ApprovedMedia>(approvedMedia[0]);

  const visibleMedia = useMemo(
    () => approvedMedia.filter((item) => item.modeKey === selectedMode && (!youngSafeMode || item.safeForYoung)),
    [selectedMode, youngSafeMode]
  );

  const selectedSpace = spaces.find((space) => space.key === selectedMedia.modeKey) || spaces[0];

  const shellStyle: React.CSSProperties = {
    minHeight: "100vh",
    color: themeColors.text,
    fontFamily: "Orbitron, sans-serif",
    padding: "28px 14px 110px",
    boxSizing: "border-box",
    background: themeColors.bg,
    overflowX: "hidden",
  };

  const pageStyle: React.CSSProperties = {
    width: "100%",
    maxWidth: 1180,
    margin: "0 auto",
  };

  const heroStyle: React.CSSProperties = {
    border: `1px solid ${themeColors.accent}55`,
    borderRadius: 26,
    padding: "24px 18px",
    background: themeColors.panel,
    boxShadow: `0 0 28px ${themeColors.accent}20`,
    textAlign: "left",
    marginBottom: 16,
  };

  const gridStyle: React.CSSProperties = {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))",
    gap: 14,
  };

  const cardStyle: React.CSSProperties = {
    border: `1px solid ${themeColors.accent}35`,
    borderRadius: 22,
    padding: 16,
    background: themeColors.cardBg,
    boxShadow: `0 0 22px ${themeColors.accent}14`,
    textAlign: "left",
    minWidth: 0,
  };

  const chipStyle: React.CSSProperties = {
    display: "inline-flex",
    margin: "4px 6px 0 0",
    padding: "6px 9px",
    borderRadius: 999,
    border: `1px solid ${themeColors.accent}33`,
    background: "rgba(255,255,255,0.05)",
    fontSize: 11,
    color: themeColors.mutedText,
    whiteSpace: "nowrap",
  };

  const buttonStyle: React.CSSProperties = {
    marginTop: 14,
    width: "100%",
    border: `1px solid ${themeColors.accent}55`,
    background: youngSafeMode ? "rgba(0,255,160,0.14)" : "rgba(255,255,255,0.07)",
    color: themeColors.text,
    borderRadius: 16,
    padding: "12px 14px",
    fontWeight: 800,
    cursor: "pointer",
  };

  const selectMode = (space: AvaSpace) => {
    setSelectedMode(space.key);

    const firstVideo = approvedMedia.find((item) => item.modeKey === space.key && (!youngSafeMode || item.safeForYoung));
    if (firstVideo) {
      setSelectedMedia(firstVideo);
    }

    window.setTimeout(() => {
      document.getElementById("ava-approved-player")?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }, 80);
  };

  return (
    <div style={shellStyle}>
      <div style={pageStyle}>
        <section style={heroStyle}>
          <div
            style={{
              color: themeColors.accent,
              fontSize: 12,
              letterSpacing: 2,
              textTransform: "uppercase",
              marginBottom: 10,
            }}
          >
            AVAAIO™ Curated Media
          </div>

          <h1
            style={{
              margin: 0,
              color: themeColors.accent,
              fontSize: "clamp(2rem, 7vw, 4rem)",
              lineHeight: 1,
            }}
          >
            Entertainment
          </h1>

          <p
            style={{
              maxWidth: 820,
              lineHeight: 1.65,
              color: themeColors.mutedText,
              fontSize: 14,
            }}
          >
            AVAAIO entertainment is a curated, safer media environment for focus,
            relaxation, training, learning, motivation, and calm cinematic experiences.
            AVAAIO does not use unrestricted public video feeds here.
          </p>

          <button type="button" onClick={() => setYoungSafeMode((v) => !v)} style={buttonStyle}>
            Young Safe Mode: {youngSafeMode ? "On" : "Off"}
          </button>

          <p
            style={{
              marginTop: 10,
              marginBottom: 0,
              fontSize: 12,
              lineHeight: 1.5,
              color: themeColors.mutedText,
            }}
          >
            Safe Mode helps reduce mature or explicit entertainment content, but automated
            and curated filtering may not detect all material.
          </p>
        </section>

        <section style={gridStyle}>
          {spaces.map((space) => {
            const isActive = selectedMode === space.key;
            const isComingSoon = space.status === "Coming Soon";

            return (
              <button
                key={space.key}
                type="button"
                onClick={() => selectMode(space)}
                style={{
                  ...cardStyle,
                  cursor: "pointer",
                  color: themeColors.text,
                  opacity: isComingSoon ? 0.72 : 1,
                  outline: isActive ? `1px solid ${themeColors.accent}` : "none",
                  background: isActive ? themeColors.buttonBg : themeColors.cardBg,
                  transform: isActive ? "translateY(-1px)" : "none",
                }}
              >
                <div
                  style={{
                    fontSize: 11,
                    color: space.status === "Available" ? "#8fffd2" : themeColors.mutedText,
                    letterSpacing: 1.4,
                    textTransform: "uppercase",
                    marginBottom: 8,
                  }}
                >
                  {space.status}
                </div>

                <h2 style={{ margin: "0 0 8px", color: themeColors.accent, fontSize: 20 }}>
                  {space.title}
                </h2>

                <p style={{ margin: 0, lineHeight: 1.55, color: themeColors.mutedText, fontSize: 13 }}>
                  {space.purpose}
                </p>

                <div style={{ marginTop: 12, display: "flex", flexWrap: "wrap", gap: 6 }}>
                  {space.examples.map((item) => (
                    <span key={item} style={chipStyle}>
                      {item}
                    </span>
                  ))}
                </div>
              </button>
            );
          })}
        </section>

        <section id="ava-approved-player" style={{ ...cardStyle, marginTop: 16 }}>
          <h2 style={{ marginTop: 0, color: themeColors.accent }}>Ava Approved Player</h2>

          <p style={{ lineHeight: 1.6, color: themeColors.mutedText, fontSize: 13 }}>
            Selected Mode: <strong>{selectedSpace.title}</strong>
            {selectedMedia ? (
              <>
                {" "}• Video: <strong>{selectedMedia.title}</strong>
              </>
            ) : null}
            . Choose a mode card above, then choose a video card below.
          </p>

          {selectedMedia ? (
            <>
              <div
                style={{
                  position: "relative",
                  width: "100%",
                  paddingTop: "56.25%",
                  borderRadius: 20,
                  overflow: "hidden",
                  border: `1px solid ${themeColors.accent}44`,
                  boxShadow: `0 0 22px ${themeColors.accent}18`,
                  background: "rgba(0,0,0,0.35)",
                  marginTop: 12,
                }}
              >
                <iframe
                  key={`${selectedMedia.modeKey}-${selectedMedia.title}-${selectedMedia.youtubeId}`}
                  title={`${selectedMedia.mode} - ${selectedMedia.title}`}
                  src={`https://www.youtube-nocookie.com/embed/${selectedMedia.youtubeId}?autoplay=0&rel=0&modestbranding=1&playsinline=1`}
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                  style={{
                    position: "absolute",
                    inset: 0,
                    width: "100%",
                    height: "100%",
                    border: 0,
                  }}
                />
              </div>

              <div style={{ marginTop: 14 }}>
                <div style={{ fontSize: 11, color: themeColors.accent2, letterSpacing: 1.4, textTransform: "uppercase" }}>
                  {selectedMedia.mode}
                </div>
                <h3 style={{ color: themeColors.accent, marginBottom: 6 }}>{selectedMedia.title}</h3>
                <p style={{ marginTop: 0, fontSize: 13, lineHeight: 1.5, opacity: 0.82 }}>
                  {selectedMedia.description}
                </p>

                <div style={{ ...gridStyle, marginTop: 14 }}>
                  {visibleMedia.map((item) => {
                    const isSelectedVideo = selectedMedia.youtubeId === item.youtubeId && selectedMedia.title === item.title;

                    return (
                      <button
                        key={item.id}
                        type="button"
                        onClick={() => {
                          setSelectedMode(item.modeKey);
                          setSelectedMedia(item);
                        }}
                        style={{
                          ...cardStyle,
                          cursor: "pointer",
                          color: themeColors.text,
                          outline: isSelectedVideo ? `3px solid ${themeColors.accent}` : `1px solid ${themeColors.accent}22`,
                          background: isSelectedVideo ? themeColors.buttonBg : themeColors.cardBg,
                          transform: isSelectedVideo ? "translateY(-2px)" : "none",
                        }}
                      >
                        <div
                          style={{
                            fontSize: 11,
                            color: isSelectedVideo ? themeColors.text : themeColors.accent2,
                            letterSpacing: 1.4,
                            textTransform: "uppercase",
                            marginBottom: 8,
                          }}
                        >
                          {isSelectedVideo ? "Now Playing" : item.mode}
                        </div>
                        <strong style={{ color: themeColors.accent }}>{item.title}</strong>
                        <p style={{ marginBottom: 0, fontSize: 12, lineHeight: 1.5, opacity: 0.82 }}>
                          {item.description}
                        </p>
                      </button>
                    );
                  })}
                </div>
              </div>
            </>
          ) : (
            <div
              style={{
                marginTop: 12,
                padding: 18,
                borderRadius: 18,
                border: `1px solid ${themeColors.accent}33`,
                background: themeColors.cardBg,
              }}
            >
              This mode is coming soon. Ava will add approved media here later.
            </div>
          )}
        </section>

        <section style={{ ...cardStyle, marginTop: 16 }}>
          <h2 style={{ marginTop: 0, color: themeColors.accent }}>Guardrails</h2>

          <p style={{ lineHeight: 1.6, color: themeColors.mutedText, fontSize: 13 }}>
            AVAAIO Entertainment is curated-first. Random user-pasted embeds,
            unrestricted searches, and mature feeds should remain disabled during launch readiness.
          </p>

          <div style={{ marginTop: 10, display: "flex", flexWrap: "wrap", gap: 6 }}>
            {blockedExamples.map((item) => (
              <span key={item} style={chipStyle}>
                Block: {item}
              </span>
            ))}
          </div>
        </section>
      </div>

    </div>
  );
}
